import { LocalizedText } from "../../LocalizedText";
import * as React from 'react';
import { useAppSelector } from '../../../hooks';
import JPanel from '../../UI/JPanel';
import { MultilineLabel } from '../../UI/MultilineLabel';
import { JButton } from '../../UI/JButton';
import Panel from '../../Panel/Panel';
import { useTranslation } from 'react-i18next';
import { getItemName } from '../../../resources/Items';
import { compressToEncodedURIComponent } from 'lz-string';
import { getBattleSummary } from '../../../util/battleSummary';

interface Props {
  onClose: () => void;
  onReplay?: () => void;
  onSave?: () => void;
  onRetry?: () => void;
  onAutoContinue?: () => void;
  autoContinue?: boolean;
  saving?: boolean;
  retrying?: boolean;
  error?: string;
}

export const FightResult: React.FC<Props> = ({ onClose, onReplay, onSave, onRetry, onAutoContinue, autoContinue, saving, retrying, error }) => {
  const { t } = useTranslation();
  const fight = useAppSelector((state) => state.fight.fight);
  const rewards = useAppSelector((state) => state.fight.rewards);
  const playerName = useAppSelector((state) => state.character?.displayName);
  const summary = React.useMemo(() => getBattleSummary(fight, playerName), [fight, playerName]);
  let rewardText = '';

  function shareBattle() {
    const compressed = compressToEncodedURIComponent(JSON.stringify(fight));

    if (compressed.length < 2400) {
      const url = window.location.href + '?access=replay&&data=' + compressed;
      navigator.clipboard.writeText(url);

      alert('Copied the url to clipboard');
    } else {
      navigator.clipboard.writeText(compressed);
      alert(
        'Fight data is too large to create url. Navigate to ' +
          window.location.href +
          '?access=replay and add the copied data to the input box.'
      );
    }
  }

  if (rewards === null) {
    return null;
  }

  if (rewards != undefined) {
    rewardText = Object.entries(rewards)
      .filter(([key, value]) => !['items'].includes(key))
      .map(([name, reward]) => {
        const labels: { [key: string]: string } = { rank: 'Rank', medals: 'Medals', exp: 'Exp', stones: 'Stones' };
        return `${t(labels[name] ?? name.toUpperCase())}: ${reward}`;
      })
      .join('\n');
  }

  return (
    <Panel name="Combat Results" moveable={false} style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
      <JPanel size={{ width: 420, height: 262 }}>
        <JPanel size={{ width: 420, height: 230 }} background="UIResource.Common.BigBG1">
          <JPanel size={{ width: 300, height: 40 }} position={{ x: 8, y: 8 }} background="UIResource.Common.BigBG3">
            <div style={{ padding: '3px 6px', fontSize: 12, lineHeight: '16px', height: 34, overflowY: 'auto' }}>
              <div style={{ fontWeight: 'bold' }}>{t(`battle_summary_${summary.result}`, { seconds: summary.seconds.toFixed(2) })}</div>
              <div>{t('battle_summary_hits', { hits: summary.hits, misses: summary.misses })}</div>
              {error && <div role="alert" style={{ color: '#a00000' }}>{error}</div>}
            </div>
          </JPanel>

          <JPanel size={{ width: 300, height: 80 }} position={{ x: 8, y: 55 }} background="UIResource.Common.BigBG3">
            <div style={{ padding: '3px 6px', fontSize: 12, lineHeight: '14px', height: 74, overflowY: 'auto' }}>
              <div>{t('battle_summary_damage', { damage: summary.damageDealt, highest: summary.highestHit })}</div>
              <div>{t('battle_summary_critical', { hits: summary.criticalHits, damage: summary.criticalDamage })}</div>
              <div>{t('battle_summary_taken', { damage: summary.damageTaken })}</div>
              <div>{t('battle_summary_reflected', { damage: summary.reflectedTaken })}</div>
              <div>{t('battle_summary_healing', { healing: summary.healing })}</div>
            </div>
          </JPanel>

          <JPanel size={{ width: 300, height: 80 }} position={{ x: 8, y: 140 }} background="UIResource.Common.BigBG3">
            <MultilineLabel size={{ width: 300, height: 80 }} position={{ x: 2, y: 2 }} text="">
              <LocalizedText text={rewardText + '\n'} />
              {rewards?.items
                ?.map((item: number | number[]) => {
                  if (Array.isArray(item)) {
                    return `${getItemName(item[0])} for ${item[1]}`;
                  }

                  return `${getItemName(item)}`;
                })
                .join('\n')}
            </MultilineLabel>
          </JPanel>

          <JButton size={{ width: 85, height: 22 }} position={{ x: 320, y: 15 }} disabled={!onSave || saving || retrying} onClick={onSave} text="Save Battle" />
          <JButton size={{ width: 85, height: 22 }} position={{ x: 320, y: 45 }} onClick={shareBattle} text="Share Battle" />
          <JButton size={{ width: 85, height: 22 }} position={{ x: 320, y: 75 }} disabled text="" />
          <JButton size={{ width: 85, height: 22 }} position={{ x: 320, y: 105 }} disabled text="" />

          <JPanel size={{ width: 63, height: 63 }} position={{ x: 331, y: 147 }} background="UIResource.Icon.Grid_HeadPortraitBase2">
            <JPanel size={{ width: 52, height: 52 }} position={{ x: 6, y: 6 }}></JPanel>
          </JPanel>
        </JPanel>

        <JButton size={{ width: 100, height: 22 }} position={{ x: 30, y: 240 }} disabled={!onReplay || saving || retrying} onClick={onReplay} text="Replay Battle" />
        {fight?.victory && fight.nextFight && onAutoContinue && <JButton size={{ width: 118, height: 22 }} position={{ x: 151, y: 240 }} disabled={saving || retrying} onClick={onAutoContinue} text={autoContinue ? 'หยุดอัตโนมัติ' : 'สู้ต่ออัตโนมัติ'} />}
        {fight?.victory === false && onRetry && <JButton size={{ width: 100, height: 22 }} position={{ x: 163, y: 240 }} disabled={saving || retrying} onClick={onRetry} text="Fight Again" />}
        <JButton size={{ width: 100, height: 22 }} position={{ x: 290, y: 240 }} disabled={retrying} onClick={onClose} text={fight?.players?.[0]?.hp <= 0 ? 'เกิดใหม่' : 'Close'} />
      </JPanel>
    </Panel>
  );
};
